import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { SessionContext } from '@benzinga/session-context';
import type { Session, Ticker } from '@benzinga/session';
import { TrendingTicker, TrendingTopic, TrendingTopicsManager } from '@benzinga/trending-topics-manager';
import { enrichTrendingTickers } from './trending-logos';

export interface UseTrendingResult {
  trendingTickers: Ticker[];
  trendingTopics: TrendingTopic[];
  isLoading: boolean;
  hasError: boolean;
  ensureLoaded: () => void;
}

const toSeedTickers = (tickers?: Ticker[]): Ticker[] =>
  (tickers ?? []).map(ticker => ({
    exchange: ticker.exchange,
    ...(ticker.logo ? { logo: ticker.logo } : {}),
    name: ticker.name ?? ticker.symbol,
    symbol: ticker.symbol,
  }));

const preloadTickerLogos = (tickers: Ticker[]): void => {
  if (typeof window === 'undefined') return;
  for (const ticker of tickers) {
    if (!ticker.logo) continue;
    const image = new Image();
    image.decoding = 'async';
    image.src = ticker.logo;
  }
};

const toTicker = (ticker: TrendingTicker): Ticker => ({ ...ticker });

export const useTrending = (
  initialTrendingTopics?: TrendingTopic[],
  initialTrendingTickers?: Ticker[],
): UseTrendingResult => {
  const session = useContext(SessionContext);

  const seedTickers = useMemo(() => toSeedTickers(initialTrendingTickers), [initialTrendingTickers]);
  const seedTopics = useMemo(
    () => (initialTrendingTopics ?? []).filter(topic => topic.taxonomy !== 'tickers'),
    [initialTrendingTopics],
  );

  const [trendingTickers, setTrendingTickers] = useState<Ticker[]>(seedTickers);
  const [trendingTopics, setTrendingTopics] = useState<TrendingTopic[]>(seedTopics);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hasLoadedRef = useRef(false);
  const pendingLoadRef = useRef<Promise<void> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    setTrendingTickers(seedTickers);
    setTrendingTopics(seedTopics);
    hasLoadedRef.current = false;
    pendingLoadRef.current = null;
    setIsLoading(false);
    setHasError(false);

    return () => {
      mountedRef.current = false;
    };
  }, [seedTickers, seedTopics]);

  const ensureLoaded = useCallback(() => {
    if (hasLoadedRef.current || pendingLoadRef.current || !session) return;
    preloadTickerLogos(seedTickers);
    setIsLoading(true);
    setHasError(false);

    const keywordsPromise =
      seedTopics.length > 0
        ? Promise.resolve(seedTopics)
        : session
            .getManager(TrendingTopicsManager)
            .getTrendingTopics()
            .then(result => {
              if (result.err) throw result.err;
              return (result.ok ?? []).filter(topic => topic.taxonomy !== 'tickers');
            });

    const tickersPromise =
      seedTickers.length > 0
        ? Promise.resolve(seedTickers)
        : session
            .getManager(TrendingTopicsManager)
            .getTrendingTickers()
            .then(result => {
              if (result.err) throw result.err;
              return (result.ok ?? []).map(toTicker);
            });

    pendingLoadRef.current = Promise.allSettled([keywordsPromise, tickersPromise])
      .then(async ([keywordsResult, tickersResult]) => {
        if (!mountedRef.current) return;

        if (keywordsResult.status === 'fulfilled') {
          setTrendingTopics(keywordsResult.value);
        }

        if (tickersResult.status === 'rejected') {
          setHasError(true);
          return;
        }

        const tickers = tickersResult.value;
        if (tickers.length === 0) {
          hasLoadedRef.current = true;
          if (keywordsResult.status === 'rejected') setHasError(true);
          return;
        }

        setTrendingTickers(tickers);

        try {
          const enriched = await enrichTrendingTickers(tickers, session as unknown as Session);
          if (!mountedRef.current) return;
          if (enriched.length > 0) {
            setTrendingTickers(enriched);
          }
        } catch {
          // Enrichment is best-effort; unenriched tickers already set above.
        }

        if (!mountedRef.current) return;
        hasLoadedRef.current = true;
        if (keywordsResult.status === 'rejected') setHasError(true);
      })
      .catch(() => {
        if (mountedRef.current) setHasError(true);
      })
      .finally(() => {
        pendingLoadRef.current = null;
        if (!mountedRef.current) return;
        setIsLoading(false);
      });
  }, [seedTickers, seedTopics, session]);

  return {
    ensureLoaded,
    hasError,
    isLoading,
    trendingTickers,
    trendingTopics,
  };
};
